import type { ActivityContext, ActivityHandle } from '../core/activity';
import './memory-math.css';

export type Pair = [expression: string, result: number];

export interface MemoryMathLevelData {
  columns: number;
  rows: number;
  texts: Pair[];
}

export interface MemoryMathLevel {
  level: number;
  objective: string;
  difficulty: number;
  data: MemoryMathLevelData[];
}

export interface MemoryMathLevelsFile {
  sourceActivity: string;
  sourceLicense: string;
  sourceProject: string;
  convertedAt: string;
  levels: MemoryMathLevel[];
}

export interface MemoryMathRunnerOptions {
  activityId: string;
  title: string;
  hint: string;
  levels: MemoryMathLevel[];
}

interface Card {
  id: number;
  pairId: number;
  face: 'expression' | 'result';
  text: string;
  matched: boolean;
  flipped: boolean;
}

export function runMemoryMathActivity(
  ctx: ActivityContext,
  opts: MemoryMathRunnerOptions,
): ActivityHandle {
  const { activityId, title, hint, levels } = opts;
  const level = levels.find((l) => l.level === ctx.level) ?? levels[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const config = level.data[0]!;
  const cards = buildCards(config.texts, config.columns * config.rows);

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">${title}</div>
      <div class="activity-bar__level">Nível ${level.level} de ${levels.length}</div>
    </header>
    <section class="activity-stage memory-math">
      <div class="feedback" aria-live="polite"></div>
      <p class="memory-math__hint">${hint}</p>
      <div class="memory-math__grid"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const grid = page.querySelector<HTMLElement>('.memory-math__grid')!;
  grid.style.gridTemplateColumns = `repeat(${config.columns}, 1fr)`;
  const backBtn = page.querySelector<HTMLElement>('.activity-bar__back')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  backBtn.addEventListener('click', () => ctx.onExit());

  const cardEls = new Map<number, HTMLButtonElement>();
  for (const c of cards) {
    const btn = document.createElement('button');
    btn.className = 'memory-card';
    btn.type = 'button';
    btn.dataset.cardId = String(c.id);
    btn.innerHTML = `
      <span class="memory-card__back">?</span>
      <span class="memory-card__front">${c.text}</span>
    `;
    btn.addEventListener('click', () => handleClick(c.id));
    grid.appendChild(btn);
    cardEls.set(c.id, btn);
  }

  let flippedIds: number[] = [];
  let matchesFound = 0;
  let locked = false;

  function handleClick(cardId: number): void {
    if (locked) return;
    const card = cards.find((c) => c.id === cardId)!;
    if (card.matched || card.flipped) return;
    card.flipped = true;
    flippedIds.push(cardId);
    cardEls.get(cardId)!.classList.add('memory-card--flipped');
    ctx.audio.play('click');

    if (flippedIds.length === 2) {
      locked = true;
      const [aId, bId] = flippedIds;
      const a = cards.find((c) => c.id === aId)!;
      const b = cards.find((c) => c.id === bId)!;
      if (a.pairId === b.pairId && a.face !== b.face) {
        a.matched = true;
        b.matched = true;
        cardEls.get(a.id)!.classList.add('memory-card--matched');
        cardEls.get(b.id)!.classList.add('memory-card--matched');
        ctx.audio.play('correct');
        showFeedback('ok');
        matchesFound++;
        flippedIds = [];
        locked = false;
        if (matchesFound * 2 === cards.length) finishLevel();
      } else {
        ctx.audio.play('wrong');
        showFeedback('err');
        window.setTimeout(() => {
          a.flipped = false;
          b.flipped = false;
          cardEls.get(a.id)!.classList.remove('memory-card--flipped');
          cardEls.get(b.id)!.classList.remove('memory-card--flipped');
          flippedIds = [];
          locked = false;
        }, 900);
      }
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓ Par!' : '✗ Não';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level!.level, matchesFound);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level!.level} concluído!</h2>
      <p class="activity-finish__text">Você encontrou ${matchesFound} pares.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level!.level < levels.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/${activityId}/${level!.level + 1}`;
      });
    }
  }

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}

function buildCards(allPairs: Pair[], totalCards: number): Card[] {
  const pairsNeeded = totalCards / 2;
  const pool = shuffle([...allPairs]).slice(0, pairsNeeded);
  const cards: Card[] = [];
  let nextId = 0;
  pool.forEach((pair, idx) => {
    cards.push({
      id: nextId++,
      pairId: idx,
      face: 'expression',
      text: pair[0],
      matched: false,
      flipped: false,
    });
    cards.push({
      id: nextId++,
      pairId: idx,
      face: 'result',
      text: String(pair[1]),
      matched: false,
      flipped: false,
    });
  });
  return shuffle(cards);
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
